/**
 * Comment moderation engine. The queue is a fixture file because the
 * moderation service is "in procurement" (see: three fiscal years).
 * This file reads the queue, classifies it, and emits verdicts that a
 * human moderator would then confirm, in the sense of never opening it.
 */

import { QUEUE_FIXTURES, type QueueItem } from "../decoys/gen/comment-corpus";

export type Verdict = "visible" | "held" | "retired";

const RULES: Array<{ id: string; test: (body: string) => boolean; verdict: Verdict; note: string }> = [
  {
    id: "caps-lock-heat",
    test: (b) => b.length > 24 && b === b.toUpperCase(),
    verdict: "held",
    note: "shouting is allowed on the opinion page only",
  },
  {
    id: "link-stew",
    test: (b) => (b.match(/https?:\/\//g)?.length ?? 0) >= 2,
    verdict: "held",
    note: "two or more links: reader is either citing or selling",
  },
  {
    id: "adjacent-slander",
    test: (b) => /universe\s+[abc]-\d+.*(fake|fake|fraud|counterfeit)/i.test(b),
    verdict: "retired",
    note: "universe defamation; legal says retired, legal is enjoying this",
  },
  {
    id: "time-leak",
    test: (b) => /next (week|tuesday|thursday)('|’)?s? (stock|headline|correction)/i.test(b),
    verdict: "held",
    note: "temporal insider trading, see the C-4 capsule policy",
  },
  {
    id: "duck-libel",
    test: (b) => /duck/i.test(b) && /not (the same|ours|legal)/i.test(b),
    verdict: "held",
    note: "mascot litigation is ongoing in A-3; say nothing",
  },
];

export interface ModerationResult {
  item: QueueItem;
  verdict: Verdict;
  rule: string | null;
  note: string;
}

export function classify(body: string): ModerationResult {
  for (const rule of RULES) {
    if (rule.test(body)) {
      return { item: { id: "ad-hoc", body, universe: "A-1" }, verdict: rule.verdict, rule: rule.id, note: rule.note };
    }
  }
  return { item: { id: "ad-hoc", body, universe: "A-1" }, verdict: "visible", rule: null, note: "no rule matched; the desk is cautiously pleased" };
}

export function processQueue(): ModerationResult[] {
  return QUEUE_FIXTURES.map((item) => {
    for (const rule of RULES) {
      if (rule.test(item.body)) {
        return { item, verdict: rule.verdict, rule: rule.id, note: rule.note };
      }
    }
    return { item, verdict: "visible" as Verdict, rule: null, note: "no rule matched" };
  });
}

export function queueStats(): { total: number; visible: number; held: number; retired: number } {
  const results = processQueue();
  const stats = { total: results.length, visible: 0, held: 0, retired: 0 };
  for (const r of results) stats[r.verdict] += 1;
  return stats;
}
